import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import amplifyConfig from '../../../config';
import axiosInstance from '../../../utils/axios';
import { useTranslation } from 'react-i18next';
import { useDispatch } from '../../../redux/store';
import { showErrorMessage } from '../../../redux/slices/millSheetSearch';
import { CUSTOMER_FIELD_NAMES } from '../../../constants';
import type { AxiosResponse } from 'axios';
import type { CustomerFieldName } from '../../../types/millSheetSearch';
import type { OptionsType } from '../../../types/options';

interface SelectCustomerProps {
  name: CustomerFieldName;
  value: string;
  disabled?: boolean;
  onChangeCustomer: (name: CustomerFieldName, value: string) => void;
}

const SelectCustomer = ({ name, value, disabled = false, onChangeCustomer }: SelectCustomerProps): React.ReactElement => {
  // console.log(name, value);
  const { t } = useTranslation();
  const endpoint: string =
    name === CUSTOMER_FIELD_NAMES.CUSTOMER1
      ? '/millsheet/customer1'
      : name === CUSTOMER_FIELD_NAMES.CUSTOMER2
      ? '/millsheet/customer2'
      : name === CUSTOMER_FIELD_NAMES.CUSTOMER3
      ? '/millsheet/customer3'
      : '';
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState<OptionsType | null>(null);
  const [options, setOptions] = useState<OptionsType[]>([]);
  const [hasCache, setHasCache] = useState<boolean>(false);
  const getOptionLabel = (option: OptionsType) => option.nameJapanese;
  const getOptionValue = (option: OptionsType) => String(option.code);

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  useEffect(() => {
    if (value === '') {
      setSelectedOption(null);
    } else if (value !== '' && !hasCache) {
      handleCustomerMenuOpen();
    } else if (value !== '' && hasCache) {
      const newSelectedOption: OptionsType | undefined = options.find((option) => option.code === value);
      // console.log(newSelectedOption);
      if (newSelectedOption !== undefined) {
        setSelectedOption(newSelectedOption);
      } else {
        setSelectedOption(null);
        // 該当する需要家が見つからなかった場合は値をリセットする
        onChangeCustomer(name, '');
      }
    }
  }, [hasCache, value]);

  // EVENT HANDLER
  // ================================================================================

  const handleCustomerMenuOpen = async () => {
    if (!hasCache) {
      try {
        const response = await axiosInstance.get<OptionsType[]>(amplifyConfig.apiGateway.URL + endpoint);
        setOptions(response.data);
        setHasCache(true);
      } catch (err) {
        dispatch(showErrorMessage(err.response.data.message));
      }
    }
  };

  const handleCustomerChange = (option: OptionsType | null): void => {
    // console.log(option);
    setSelectedOption(option);
    const newValue: string = option.code;
    onChangeCustomer(name, newValue);
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <Select
        id={name}
        name={name}
        className="react-select-container"
        classNamePrefix="react-select"
        value={selectedOption}
        options={options}
        isDisabled={disabled}
        onMenuOpen={handleCustomerMenuOpen}
        onChange={handleCustomerChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        placeholder={t('dropdown.default')}
      />
    </>
  );
};

export default SelectCustomer;
